/**
 * Display labels and canonical orderings for the capabilities modal.
 *
 * ORDER MATTERS: the filter chips are derived from the tools actually present,
 * but they are rendered in the order declared here — not in whatever order the
 * tool list happens to arrive in — so the chip row is stable across roles and
 * feature flags.
 */

export const ALL_KEY = 'all';

export const CATEGORY_LABELS: Record<string, string> = {
  lookup: 'Lookup',
  search: 'Search',
  list: 'List',
  create: 'Create',
  edit: 'Edit',
  communicate: 'Communicate',
  integrations: 'Integrations',
};

export const USE_CASE_LABELS: Record<string, string> = {
  operations: 'Operations',
  sales: 'Sales',
  marketing: 'Marketing',
  general: 'General',
};

export const ENTITY_LABELS: Record<string, string> = {
  contact: 'Contact',
  company: 'Company',
  employee: 'Employee',
  ticket: 'Ticket',
  time_entry: 'Time entry',
  activity: 'Activity',
  email: 'Email',
  calendar: 'Calendar',
  drive: 'Drive',
  quickbooks: 'QuickBooks',
  web: 'Web',
  deal: 'Deal',
  task: 'Task',
  offering: 'Offering',
  msp_doc: 'MSP Doc',
  recurring_transaction: 'Recurring Transaction',
  social: 'Social',
  contract: 'Contract',
  slack: 'Slack',
  // Added 2026-09: these twelve were live in the data and absent from both this
  // map and ENTITY_ORDER, so 91 capabilities could not be reached by the entity
  // filter at all (they were only findable by free-text search, which matches
  // the raw entity key). Keep this in step with `ChatToolEntity` in
  // apps/hub/lib/core/chat/tools/constants/types.ts — that union is the source.
  recurring_charge: 'Recurring Charge',
  plaid_transaction: 'Bank Transaction',
  email_campaign: 'Email Campaign',
  skill: 'Skill',
  slack_file: 'Slack File',
  drive_file: 'Drive File',
  credit_bureau: 'Credit Bureau',
  web_analytics: 'Web Analytics',
  ios_diagnostic: 'iOS Diagnostic',
  webinar: 'Webinar',
  image: 'Image',
  routine: 'Routine',
};

export const ROLE_LABELS: Record<string, string> = {
  wtc_admin: 'Admin',
  wtc_employee: 'Employee',
  wtc_crm: 'CRM User',
};

export const USE_CASE_ORDER: readonly string[] = ['operations', 'sales', 'marketing', 'general'];

/**
 * The sidebar spine: what KIND of thing a capability does.
 *
 * Read order, not alphabetical — the passive verbs first, then the ones that
 * change something, then the ones that leave the building. A reader scanning
 * for "can it send an email" and a reader scanning for "what can it just tell
 * me" both find their half in one place.
 */
export const CATEGORY_ORDER: readonly string[] = [
  'lookup',
  'search',
  'list',
  'create',
  'edit',
  'communicate',
  'integrations',
];

export const ENTITY_ORDER: readonly string[] = [
  'contact',
  'company',
  'employee',
  'ticket',
  'time_entry',
  'activity',
  'email',
  'calendar',
  'drive',
  'drive_file',
  'quickbooks',
  'plaid_transaction',
  'recurring_transaction',
  'recurring_charge',
  'web',
  'web_analytics',
  'deal',
  'task',
  'routine',
  'offering',
  'msp_doc',
  'social',
  'email_campaign',
  'webinar',
  'contract',
  'credit_bureau',
  'slack',
  'slack_file',
  'skill',
  'image',
  'ios_diagnostic',
];
